import {
  useAllInvestmentAccountsQuery,
  useCreateInvestmentAdjustmentMutation,
  useInvestmentActivityQuery,
  useInvestmentGapsQuery,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import * as React from 'react';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import InputGroup from 'src/InputGroup';
import {TextInput} from 'src/inputs/TextInput';
import {formatMoney, formatShares, formatSigned} from 'src/InvestmentAccountsView/money';
import {
  adjustmentFor,
  choicesFor,
  closesGap,
  isShareGap,
  RECONCILE_REFETCH,
  suggestedChoice,
} from 'src/InvestmentAccountsView/reconcileModel';
import {cn} from 'src/lib/utils';
import {Modal} from 'src/Modal';
import type {InvestmentActivityQuery} from '@transactions/graphql';
import type {DateTime} from 'luxon';
import type {Choice, Gap} from 'src/InvestmentAccountsView/reconcileModel';
import type {SystemProps} from 'src/Modals';

export type ReconcileGapModalUserProps = {
  gap: Gap;
  onRecorded?: (adjustmentId: string, label: string) => void;
};

/** Dollars typed by hand ("−1,421.40", "$12") as cents; null when not a number. */
const parseCents = (text: string): number | null => {
  const cleaned = text.replace(/[$,\s]/g, '').replace('−', '-');
  if (cleaned === '' || Number.isNaN(Number(cleaned))) return null;
  return Math.round(Number(cleaned) * 100);
};

/**
 * What an activity row moved, as the checks count it: its shares, then its
 * cash ("—" when the checks can't count it). A row in another currency
 * isn't counted, so it shows the cash it moved in its own.
 */
const movedBy = (row: InvestmentActivityQuery['investmentActivity'][number]) => {
  const {effect} = row;
  const unknown = effect.foreign ? effect.foreignCash == null : effect.cashUnknown;
  const cash = effect.foreign ? (effect.foreignCash ?? 0) : effect.cash;
  return [
    effect.quantity !== 0 ? formatShares(effect.quantity) : null,
    unknown ? '—' : cash !== 0 || effect.quantity === 0 ? formatSigned(cash, row.currency) : null,
  ]
    .filter((part) => part != null)
    .join(' · ');
};

const centsText = (cents: number | null | undefined) => (cents == null ? '' : (cents / 100).toFixed(2));

/**
 * What a gap was: its evidence and the activity around it, then a kind,
 * its date and amounts, and a note. Recording adds an adjustment that the
 * checks and the split count like a feed row.
 */
export const ReconcileGapModal = (props: ReconcileGapModalUserProps & SystemProps) => {
  const {gap: opened} = props;
  // Its account (name, and the currency its amounts are in) as it is now: a
  // sync can change an account's currency.
  const {
    data: accountsData,
    loading: accountsRefreshing,
    error: accountsError,
    refetch: refetchAccounts,
  } = useAllInvestmentAccountsQuery({notifyOnNetworkStatusChange: true});
  const account = accountsData?.allInvestmentAccounts.find((a) => a._id === opened.accountId);
  const currency = account?.currency ?? 'USD';
  // The gap as the checks have it now, not as it was when the sheet opened:
  // a refresh (a sync, another record) may have moved or closed it. Any
  // refetch shows as loading.
  const {
    data: gapsData,
    loading: gapsRefreshing,
    error: gapsError,
    refetch: refetchGaps,
  } = useInvestmentGapsQuery({notifyOnNetworkStatusChange: true});
  const same = (other: Gap) => other.accountId === opened.accountId && other.securityId === opened.securityId;
  const live = gapsData == null ? opened : gapsData.investmentGaps.find(same);
  // Still the same gap, and its own card: not closed (or closed and opened
  // again: a new gap, with its own since), and not since paired into a trade
  // (whose share gap's sheet records both legs).
  const current = live != null && live.pairedSecurityId == null && live.since === opened.since ? live : null;
  const gap = current ?? opened;
  const formId = React.useId();
  const choices = choicesFor(gap);

  // Every field follows the live gap (its suggestion, its size) until the
  // person sets it, so a refresh's better evidence carries through. A pick
  // holds only while it still fits the gap (a refresh can turn it around).
  const [picked, setPicked] = React.useState<Choice | null>(null);
  const choice = choices.find((c) => c.key === picked?.key) ?? suggestedChoice(gap);
  const [typedDate, setTypedDate] = React.useState<DateTime | null>(null);
  const date = typedDate ?? parseDate(gap.suggestion.date);
  const [typedCash, setTypedCash] = React.useState<string | null>(null);
  const cash = typedCash ?? centsText(gap.suggestion.cash ?? gap.cash);
  // A gap is a difference of stored quantities, so it carries float noise; ten significant digits drop it.
  const [typedShares, setTypedShares] = React.useState<string | null>(null);
  const shares = typedShares ?? (gap.quantity != null ? String(Number(gap.quantity.toPrecision(10))) : '');
  const [typedNote, setTypedNote] = React.useState<string | null>(null);
  const note = typedNote ?? choice.note;
  const [error, setError] = React.useState<string | null>(null);
  const [create, {loading}] = useCreateInvestmentAdjustmentMutation(RECONCILE_REFETCH);

  // The account's activity over the gap's span: what the feed did say. The
  // checks apply a row on the date it posted, so these are the rows that
  // posted from the observation before the gap opened (exclusive in the
  // checks, shown for context) to the latest one: anything later isn't in
  // the gap yet. It's always fetched fresh (a sync may have added rows
  // since; a sync's refresh refetches it).
  const from = parseDate(gap.after);
  const to = parseDate(gap.day);
  const {
    data: activityData,
    loading: activityLoading,
    error: activityError,
    refetch: refetchActivity,
  } = useInvestmentActivityQuery({
    variables: {from: from.toISODate(), to: to.toISODate(), accountId: opened.accountId, byPosting: true},
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const activity = (activityData?.investmentActivity ?? [])
    .filter((row) => !row.effect.cancelled)
    .sort((a, b) => a.date.localeCompare(b.date));

  const pick = (key: string) => {
    setPicked(choices.find((c) => c.key === key) ?? choice);
  };

  const cents = parseCents(cash);
  const quantity = shares.trim() === '' ? null : Number(shares);
  // A trade's cash only when a cash gap moved with it; otherwise its cash
  // would open one.
  const needsCash = !choice.shares || (choice.kind === 'trade' && gap.suggestion.cash != null);
  const closes = closesGap(gap, choice, {date: date.toISODate() ?? gap.since, cents, quantity});
  const valid =
    // Cash alone must be some amount; a trade's may be none, or left out.
    (!needsCash || (choice.shares ? cash.trim() === '' || cents != null : cents != null && cents !== 0)) &&
    (!choice.shares || (quantity != null && !Number.isNaN(quantity) && quantity !== 0)) &&
    (choice.kind !== 'writeoff' || note.trim() !== '') &&
    closes.date &&
    closes.shares &&
    (!needsCash || closes.cash);

  // Recording needs the evidence fresh: the gap, its activity and its
  // account all loaded, without error, since the last refresh.
  const fresh =
    !gapsRefreshing &&
    gapsError == null &&
    !activityLoading &&
    activityError == null &&
    !accountsRefreshing &&
    accountsError == null &&
    account != null;
  const closed = current == null;
  const ready = !loading && fresh && !closed && valid;
  const retry = () => {
    if (gapsError != null) void refetchGaps().catch(() => undefined);
    if (activityError != null) void refetchActivity().catch(() => undefined);
    if (accountsError != null) void refetchAccounts().catch(() => undefined);
  };
  // While it records, the sheet stays: closing it could let the gap be recorded again.
  const close = () => {
    if (!loading) props.onRequestClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setError(null);
    try {
      const result = await create({
        variables: adjustmentFor(gap, choice, {
          date: date.toISODate() ?? gap.since,
          cash: needsCash ? cents : null,
          quantity: choice.shares ? quantity : null,
          note,
        }),
      });
      const id = result.data?.createInvestmentAdjustment._id;
      if (id != null) props.onRecorded?.(id, choice.label);
      props.onRequestClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't record it");
      // Refused, maybe because the gap has moved on: fetch it as it is now.
      void refetchGaps().catch(() => undefined);
    }
  };

  const title = gap.securityLabel ?? 'Cash';
  const size = isShareGap(gap) ? formatShares(gap.quantity ?? 0) : formatSigned(gap.cash ?? 0, currency);

  return (
    <Modal
      onRequestClose={close}
      header="What was this?"
      footer={
        <>
          <Button variant="outline" disabled={loading} onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={formId} disabled={!ready}>
            {choice.kind === 'writeoff' ? 'Write off' : `Record ${choice.label.toLowerCase()}`}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate font-medium">{title}</div>
            <div className="text-muted-foreground truncate text-sm">
              {account?.name ?? 'Account'} · since {gap.since}
            </div>
          </div>
          <span className="shrink-0 font-semibold tabular-nums">{size}</span>
        </div>
        {closed && (
          <div role="status" className="text-positive text-sm">
            {live == null ? 'Balanced now' : live.pairedSecurityId != null ? 'Part of a trade now' : 'A new gap now'}
          </div>
        )}
        {gap.suggestion.evidence != null && (
          <div className="text-muted-foreground text-sm">↳ {gap.suggestion.evidence}</div>
        )}
        {(gapsError != null || activityError != null || accountsError != null) && (
          <div
            role="alert"
            className="bg-destructive/10 text-destructive flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm"
          >
            <span>
              {gapsError != null
                ? "Couldn't refresh the gap."
                : activityError != null
                  ? "Couldn't load activity."
                  : "Couldn't refresh the account."}
            </span>
            <button type="button" className="underline" onClick={retry}>
              Retry
            </button>
          </div>
        )}
        {(activity.length > 0 || (activityLoading && activityData == null)) && (
          <div className="flex flex-col gap-1">
            <div className="text-muted-foreground text-xs tracking-wide uppercase">Activity</div>
            {activityLoading && activityData == null && (
              <div className="text-muted-foreground text-sm">Loading activity...</div>
            )}
            {activity.map((row) => (
              <div key={row._id} className="flex justify-between gap-3 text-sm">
                <span className="min-w-0 truncate">
                  <span className="text-muted-foreground tabular-nums">{row.date}</span> {row.name}
                </span>
                <span className="shrink-0 tabular-nums">
                  {movedBy(row)}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Kind">
          {choices.map((c) => (
            <button
              key={c.key}
              type="button"
              aria-pressed={c.key === choice.key}
              onClick={() => pick(c.key)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm',
                c.key === choice.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="text-muted-foreground text-sm">
          Counts as <span className="text-foreground font-medium">{choice.lands}</span>
        </div>
        <InputGroup label="Date" htmlFor={`${formId}-date`}>
          <DatePicker id={`${formId}-date`} selectedDate={date} onChange={setTypedDate} invalid={!closes.date} />
        </InputGroup>
        {choice.shares && (
          <InputGroup label="Shares" htmlFor={`${formId}-shares`}>
            <TextInput
              id={`${formId}-shares`}
              value={shares}
              onChange={setTypedShares}
              inputMode="decimal"
              aria-invalid={!closes.shares}
            />
          </InputGroup>
        )}
        {needsCash && (
          <InputGroup label="Cash" htmlFor={`${formId}-cash`}>
            <TextInput
              id={`${formId}-cash`}
              aria-invalid={!closes.cash}
              value={cash}
              onChange={setTypedCash}
              inputMode="decimal"
              placeholder={formatMoney(0, currency)}
            />
          </InputGroup>
        )}
        <InputGroup label="Note" htmlFor={`${formId}-note`}>
          <TextInput
            id={`${formId}-note`}
            value={note}
            onChange={setTypedNote}
            placeholder={choice.kind === 'writeoff' ? 'Rounding' : ''}
          />
        </InputGroup>
        {error != null && <div className="text-destructive text-sm">{error}</div>}
      </form>
    </Modal>
  );
};
